import useEventCallback from '@unifyapps/hooks/useEventCallback';
import _keys from 'lodash/keys';
import _pick from 'lodash/pick';
import type { JsonMap } from '../../../../../analytics/analytics';
import { useAnalytics } from '../../../../../analytics/useAnalytics';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from '../../const';

function useSendCustomEvent() {
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const { track } = useAnalytics();
  const customEvents = useGlobalAppStateStore().hooks.record.useCustomEvents();

  const sendCustomEvent: ActionHandlerType = useEventCallback(
    ({ action, actionContext }: OnActionArgs) => {
      const payload = action.payload as { eventId: string; eventPayload?: unknown };
      const { eventId, eventPayload: unComputedEventPayload } = payload;
      const customEvent = customEvents?.find((event) => event.id === eventId);

      if (!customEvent) {
        console.error('Custom event not found for ID:', eventId);
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      const customEventSchemaProperties = customEvent?.eventSchema?.properties;
      // pick only the properties that are present in the custom event schema
      const effectiveEventPayload = _pick(unComputedEventPayload, _keys(customEventSchemaProperties));
      
      const { value: eventPayload } = getNoCodeComputedData<{
        value: JsonMap;
      }>(
        {
          value: effectiveEventPayload,
        },
        actionContext,
      );

      track(customEvent?.eventName, {
        activityCategory: 'CUSTOM',
        attributes: eventPayload,
      });
      return Promise.resolve(SHOULD_PROPAGATE_UP);
    },
  );

  return { sendCustomEvent };
}

export default useSendCustomEvent;
